import { CommentVisibility } from '../../clients/rust-api-client';
import { BadgeColor } from '../../billing/enumLabels';
import { CommentSubjectKind, useCommentSubjectKind } from './commentSubject';

/**
 * Everything the UI says about a thread, in one place.
 *
 * The failure this feature exists to prevent is a manager posting an
 * internal note into the shared thread. The defence is that the composer
 * names its destination three times — in the helper text, in its tint, and
 * on the submit button — and all three live here so that a change to one
 * is a change in front of the other two.
 */
export interface ThreadCopy {
  /** For the tab and the badge on a comment. */
  label: string;
  badgeColor: BadgeColor;
  /** Sits directly above the textarea. Says who reads this. */
  composerHint: string;
  /** The submit button. Never a bare "Post" — always the destination. */
  submitLabel: string;
  /** Applied to the composer (border and fill), so peripheral vision
   *  separates the two. */
  composerTint: string;
  /** Shown instead of a list when this thread has nothing in it. */
  emptyText: string;
  /** The empty composer's placeholder, when it is not a reply. */
  placeholder: string;
}

/**
 * Keyed by subject first, because "shared" names a different audience on
 * each (see commentSubject.ts). A work order's copy must never mention the
 * homeowner, and must not promise an email: work-order comments send no
 * notifications yet.
 */
export const THREAD_COPY: Record<
  CommentSubjectKind,
  Record<CommentVisibility, ThreadCopy>
> = {
  violation: {
    internal: {
      label: 'Internal',
      badgeColor: 'amber',
      composerHint:
        'Only your team can see this. The homeowner and the board are not notified.',
      submitLabel: 'Post internal note',
      composerTint: 'border-amber-500/50 bg-amber-500/[0.06]',
      emptyText: 'No internal notes on this violation yet.',
      placeholder: 'Leave an internal note…',
    },
    shared: {
      label: 'Shared with homeowner',
      badgeColor: 'sky',
      composerHint:
        'The homeowner will see this and be emailed about it. The board can read it too.',
      submitLabel: 'Reply to homeowner',
      composerTint: 'border-line bg-raised',
      emptyText: 'Nothing has been shared with the homeowner yet.',
      placeholder: 'Leave a comment for the homeowner…',
    },
  },
  work_order: {
    internal: {
      label: 'Internal',
      badgeColor: 'amber',
      composerHint: 'Only your team can see this. The board cannot.',
      submitLabel: 'Post internal note',
      composerTint: 'border-amber-500/50 bg-amber-500/[0.06]',
      emptyText: 'No internal notes on this work order yet.',
      placeholder: 'Leave an internal note…',
    },
    shared: {
      label: 'Shared with board',
      badgeColor: 'sky',
      composerHint:
        "The HOA's board can read this. Homeowners never see work orders.",
      submitLabel: 'Post to board',
      composerTint: 'border-line bg-raised',
      emptyText: 'Nothing has been shared with the board yet.',
      placeholder: 'Leave a comment for the board…',
    },
  },
  // The household is the homeowner party on a reservation — everyone who
  // lives in the booked home reads the shared thread, and management's
  // replies there are emailed to them (#640 phase 2).
  reservation: {
    internal: {
      label: 'Internal',
      badgeColor: 'amber',
      composerHint:
        'Only your team can see this. The household and the board are not notified.',
      submitLabel: 'Post internal note',
      composerTint: 'border-amber-500/50 bg-amber-500/[0.06]',
      emptyText: 'No internal notes on this reservation yet.',
      placeholder: 'Leave an internal note…',
    },
    shared: {
      label: 'Shared with household',
      badgeColor: 'sky',
      composerHint:
        'Everyone in the household will see this and be emailed about it. The board can read it too.',
      submitLabel: 'Reply to household',
      composerTint: 'border-line bg-raised',
      emptyText: 'Nothing has been shared with the household yet.',
      placeholder: 'Leave a message for the household…',
    },
  },
};

export const threadCopy = (
  visibility: CommentVisibility,
  subject: CommentSubjectKind = 'violation'
): ThreadCopy => THREAD_COPY[subject][visibility];

/** `threadCopy` for the thread being rendered. */
export const useThreadCopy = (visibility: CommentVisibility): ThreadCopy =>
  threadCopy(visibility, useCommentSubjectKind());
